import { clsx } from 'clsx';
import { type ReactNode, useId } from 'react';

/**
 * Pick one of a few options (1s and 2s vs 2s and 3s, first to 11/15/21). Built on native radio
 * inputs, so arrow keys and screen readers work without extra code. The chosen segment is
 * lavender, pressed in, and carries a check, so the state reads without color.
 *
 *   <SegmentedControl label="Scoring" value={scoring} onChange={setScoring}
 *     options={[{ value: 'ones_twos', label: '1s & 2s' }, { value: 'twos_threes', label: '2s & 3s' }]} />
 */
export interface SegmentOption<T extends string> {
  value: T;
  label: ReactNode;
  disabled?: boolean;
}

export interface SegmentedControlProps<T extends string> {
  label: string;
  /** Print the label above the control. Default true. */
  showLabel?: boolean;
  options: readonly SegmentOption<T>[];
  value: T;
  onChange: (v: T) => void;
  size?: 'md' | 'lg';
  name?: string;
  className?: string;
}

export function SegmentedControl<T extends string>({
  label,
  showLabel = true,
  options,
  value,
  onChange,
  size = 'md',
  name,
  className,
}: SegmentedControlProps<T>) {
  const id = useId();
  const group = name ?? id;
  return (
    <fieldset className={clsx('m-0 min-w-0 border-0 p-0', className)}>
      <legend className={showLabel ? 'mb-1.5 text-sm font-bold uppercase tracking-wide' : 'sr-only'}>{label}</legend>
      <div className="flex rounded-tts border-(length:--tts-border) border-ink bg-white shadow-btn">
        {options.map((o, i) => {
          const checked = o.value === value;
          return (
            <label
              key={o.value}
              className={clsx(
                'relative flex flex-1 cursor-pointer items-center justify-center gap-1 px-2 text-center font-display font-black uppercase italic leading-tight text-ink has-[:focus-visible]:z-10 has-[:focus-visible]:outline-3 has-[:focus-visible]:-outline-offset-8 has-[:focus-visible]:outline-dashed has-[:focus-visible]:outline-ink has-[:disabled]:cursor-not-allowed has-[:disabled]:opacity-50',
                size === 'lg' ? 'min-h-14 text-base' : 'min-h-11 text-sm',
                i > 0 && 'border-l-(length:--tts-border) border-ink',
                checked && 'bg-lavender shadow-[inset_3px_3px_0_0_var(--color-ink)]',
              )}
            >
              <input
                type="radio"
                name={group}
                value={o.value}
                checked={checked}
                disabled={o.disabled}
                onChange={() => onChange(o.value)}
                className="sr-only"
              />
              {checked && (
                <svg viewBox="0 0 24 24" width={14} height={14} aria-hidden="true" className="shrink-0">
                  <path
                    d="m4.5 12.5 5 5 10-11"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={4}
                    strokeLinecap="round"
                  />
                </svg>
              )}
              <span>{o.label}</span>
            </label>
          );
        })}
      </div>
    </fieldset>
  );
}
